<script lang="ts">
  import { inertia, usePage, router } from "@inertiajs/svelte";
  import * as Sidebar from "$lib/components/ui/sidebar/index.js";
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";
  import {
    Home,
    BarChart3,
    Users,
    Settings,
    LogOut,
    ChevronsUpDown,
    Plus,
    ImagePlay,
    LayoutTemplate
  } from "lucide-svelte";

  // 1. 获取全局共享的站点数据
  const page = usePage();
  const currentSite = $derived($page.props.current_site);
  const sites = $derived($page.props.available_sites || []);
  const currentTenant = $derived($page.props.current_tenant);

  // 2. 站点切换函数
  function switchSite(siteId: number) {
    if (siteId === currentSite?.id) return;
    
    router.post('/sites/switch', { site_id: siteId }, {
      preserveState: false, // 切换站点后通常需要刷新整个状态
      onSuccess: () => {
        // 后端会重定向，这里可以加个 loading 状态
      }
    });
  }

  let isMultiSite = $derived(sites.length > 1);
</script>

<Sidebar.Root collapsible="icon" class="border-r">
  <Sidebar.Header>
    <Sidebar.Menu>
      <Sidebar.MenuItem>
        {#if isMultiSite}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger class="w-full">
              {@render SiteButton(currentSite)}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              sideOffset={4}
            >
              <DropdownMenu.Label class="text-xs text-muted-foreground">切换站点</DropdownMenu.Label>
              {#each sites as site}
                <DropdownMenu.Item 
                  class="gap-2 p-2 cursor-pointer" 
                  onclick={() => switchSite(site.id)}
                >
                  <div class="flex size-6 shrink-0 items-center justify-center rounded-sm border bg-primary text-primary-foreground text-[10px] font-bold">
                    {site.name[0]}
                  </div>
                  <span class="flex-1 truncate {site.id === currentSite?.id ? 'font-bold text-primary' : ''}">
                    {site.name}
                  </span>
                  {#if site.id === currentSite?.id}
                    <div class="size-1.5 rounded-full bg-primary"></div>
                  {/if}
                </DropdownMenu.Item>
              {/each}
              <DropdownMenu.Separator />
              <DropdownMenu.Item class="gap-2 p-2" onclick={() => router.get('/sites/new')}>
                <div class="flex size-6 items-center justify-center rounded-md border bg-background">
                  <Plus class="size-4" />
                </div>
                <div class="font-medium text-muted-foreground">创建新站点</div>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        {:else}
          {@render SiteButton(currentSite, false)}
        {/if}
      </Sidebar.MenuItem>
    </Sidebar.Menu>
  </Sidebar.Header>

  <Sidebar.Content>
    <Sidebar.Group>
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              asChild
              isActive={$page.url.startsWith("/dashboard")}
              tooltip="控制台"
            >
              <a href="/dashboard" use:inertia class="flex items-center gap-3 w-full">
                <Home class="size-4 shrink-0" />
                <span class="truncate">控制台</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              asChild
              isActive={$page.url.startsWith("/medias")}
              tooltip="素材库"
            >
              <a href="/medias" use:inertia class="flex items-center gap-3 w-full">
                <ImagePlay class="size-4 shrink-0" />
                <span class="truncate">素材库</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              asChild
              isActive={$page.url.startsWith("/sites")}
              tooltip="站点"
            >
              <a href="/sites" use:inertia class="flex items-center gap-3 w-full">
                <BarChart3 class="size-4 shrink-0" />
                <span class="truncate">站点管理</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              asChild
              isActive={$page.url.startsWith("/pages")}
              tooltip="页面"
            >
              <a href="/pages" use:inertia class="flex items-center gap-3 w-full">
                <BarChart3 class="size-4 shrink-0" />
                <span class="truncate">页面管理</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              asChild
              isActive={$page.url.startsWith("/templates")}
              tooltip="模板"
            >
              <a href="/templates" use:inertia class="flex items-center gap-3 w-full">
                <LayoutTemplate class="size-4 shrink-0" />
                <span class="truncate">模板库</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>

    <Sidebar.Group class="mt-auto">
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              asChild
              isActive={$page.url.startsWith("/settings")}
              tooltip="设置"
            >
              <a href="/settings" use:inertia class="flex items-center gap-3 w-full">
                <Settings class="size-4 shrink-0" />
                <span class="truncate">站点设置</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>
  </Sidebar.Content>

  <Sidebar.Footer>
    <Sidebar.Menu>
      <Sidebar.MenuItem>
        <Sidebar.MenuButton tooltip="Logout" onclick={() => router.delete('/logout')}>
          <LogOut class="size-4 shrink-0" />
          <span class="group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden">
            退出登录
          </span>
        </Sidebar.MenuButton>
      </Sidebar.MenuItem>
    </Sidebar.Menu>
  </Sidebar.Footer>
</Sidebar.Root>

{#snippet SiteButton(site, showArrow = true)}
  <Sidebar.MenuButton
    size="lg"
    class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
  >
    <div class="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-xl group-data-[state=collapsed]:mx-auto">
      {site?.name?.[0] || 'S'}
    </div>
    <div class="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden ml-1">
      <span class="truncate font-semibold">{site?.name || '选择站点'}</span>
      <span class="truncate text-[10px] text-muted-foreground uppercase">{currentTenant?.plan_level || '免费版'}</span>
    </div>
    {#if showArrow}
      <ChevronsUpDown class="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden opacity-50" />
    {/if}
  </Sidebar.MenuButton>
{/snippet}